import { useCallback, useEffect, useRef, type MouseEvent } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useCanvasFreeElementContext } from '../../canvas-element-tool/CanvasFreeElementContext';
import { CanvasElementType } from '../../canvas-types';
import { uniqueId } from '../../../../lib/uniqueId';
import type { RfNodeStickyNote } from '../../canvas-types/nodeTypesData';
import {
  STICKY_NOTE_DEFAULT_COLOR,
  STICKY_NOTE_DEFAULT_WIDTH,
  STICKY_NOTE_DEFAULT_HEIGHT,
} from './consts';

export const StickyNoteCreationTool = () => {
  const { setNodes, screenToFlowPosition } = useReactFlow();
  const { createStickyNoteActive, setCreateElementActive } = useCanvasFreeElementContext();
  const temporaryNodeIdRef = useRef<string | null>(null);

  const setCreateStickyNoteActive = useCallback(
    (active: boolean) => setCreateElementActive(active ? 'sticky-note' : null),
    [setCreateElementActive],
  );

  // Create temporary sticky note on mount
  useEffect(() => {
    if (!createStickyNoteActive) return;

    const tempNodeId = `temp-${uniqueId()}`;
    temporaryNodeIdRef.current = tempNodeId;

    const temporaryNode: RfNodeStickyNote = {
      id: tempNodeId,
      type: CanvasElementType.STICKY_NOTE,
      position: { x: 0, y: 0 }, // Will be updated on mouse move
      data: {
        color: STICKY_NOTE_DEFAULT_COLOR,
        richText: '',
        draft: true,
        textScale: 1,
      },
      width: STICKY_NOTE_DEFAULT_WIDTH,
      height: STICKY_NOTE_DEFAULT_HEIGHT,
      selectable: false,
      draggable: false,
    };

    setNodes((nodes) => [...nodes, temporaryNode]);

    // Cleanup on unmount
    return () => {
      if (temporaryNodeIdRef.current) {
        const tempId = temporaryNodeIdRef.current;
        setNodes((nodes) => nodes.filter((n) => n.id !== tempId));
      }
    };
  }, [createStickyNoteActive, setNodes]);

  // Handle Escape key to cancel creation
  useEffect(() => {
    if (!createStickyNoteActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCreateStickyNoteActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [createStickyNoteActive, setCreateStickyNoteActive]);

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!temporaryNodeIdRef.current) return;

      const position = screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      // Center the sticky note on the cursor
      const centeredPosition = {
        x: position.x - STICKY_NOTE_DEFAULT_WIDTH / 2,
        y: position.y - STICKY_NOTE_DEFAULT_HEIGHT / 2,
      };

      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === temporaryNodeIdRef.current ? { ...node, position: centeredPosition } : node,
        ),
      );
    },
    [screenToFlowPosition, setNodes],
  );

  const handleClick = useCallback(() => {
    if (!temporaryNodeIdRef.current) return;

    const tempId = temporaryNodeIdRef.current;
    const newNodeId = uniqueId();

    // Get the temporary node position
    setNodes((nodes) => {
      const tempNode = nodes.find((n) => n.id === tempId) as RfNodeStickyNote | undefined;
      if (!tempNode) return nodes;

      const permanentNode: RfNodeStickyNote = {
        id: newNodeId,
        type: CanvasElementType.STICKY_NOTE,
        position: tempNode.position,
        data: {
          color: STICKY_NOTE_DEFAULT_COLOR,
          richText: '',
          draft: false,
          freshlyCreated: true,
          textScale: 1,
        },
        width: STICKY_NOTE_DEFAULT_WIDTH,
        height: STICKY_NOTE_DEFAULT_HEIGHT,
        dragHandle: '.drag-handle',
      };

      // Remove temporary node and add permanent node
      return [...nodes.filter((n) => n.id !== tempId), permanentNode];
    });

    temporaryNodeIdRef.current = null;
    setCreateStickyNoteActive(false);
  }, [setNodes, setCreateStickyNoteActive]);

  if (!createStickyNoteActive) return null;

  return (
    <div
      className="nopan nodrag canvas-free-element-overlay"
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    />
  );
};
